import React, { useState, useEffect, useContext, useRef } from "react";
import { Modal } from "react-bootstrap";
import { GlobalContext } from "../Context/GlobalContext";

const WaistModal = ({ show, onClose, onSave, initialWaist, initialUnit }) => {
  const { translatedLabels } = useContext(GlobalContext);
  const authLabel = translatedLabels?.auth;
  const screenLabels = translatedLabels?.common;

  const integerListRef = useRef(null);
  const decimalListRef = useRef(null);

  const [unit, setUnit] = useState(initialUnit || "inch");
  const [integerPart, setIntegerPart] = useState(0);
  const [decimalPart, setDecimalPart] = useState(0);

  const convertWaist = (waist, fromUnit, toUnit) => {
    if (fromUnit === toUnit) return waist;
    return fromUnit === "cm"
      ? (waist / 2.54).toFixed(1)
      : (waist * 2.54).toFixed(0);
  };

  useEffect(() => {
    if (show) {
      setUnit(initialUnit ? initialUnit : "inch");
    }
  }, [show, initialUnit]);

  useEffect(() => {
    if (show) {
      const waist = parseFloat(initialWaist || 0);
      const formattedWaist =
        initialUnit !== unit ? convertWaist(waist, initialUnit, unit) : waist;

      const [intPart, decPart] = formattedWaist.toString().split(".");
      setIntegerPart(parseInt(intPart) || 0);
      setDecimalPart(
        unit === "inch" ? parseInt(decPart?.padEnd(1, "0")) || 0 : 0,
      );
    }
  }, [show, initialWaist, initialUnit, unit]);

  useEffect(() => {
    if (!show) return;

    const handleScroll = (ref, setValue) => {
      if (!ref.current) return;

      const container = ref.current;
      const items = Array.from(container.querySelectorAll(".picker-item"));
      const containerRect = container.getBoundingClientRect();
      const centerY = containerRect.top + containerRect.height / 2;

      let closest = null;
      let closestDistance = Infinity;

      for (let item of items) {
        const itemRect = item.getBoundingClientRect();
        const itemCenter = itemRect.top + itemRect.height / 2;
        const distance = Math.abs(itemCenter - centerY);

        if (distance < closestDistance) {
          closest = item;
          closestDistance = distance;
        }
      }

      if (closest) {
        const value = parseInt(closest.textContent);
        if (!isNaN(value)) {
          setValue(value);
        }
      }
    };

    const onScrollInteger = () => handleScroll(integerListRef, setIntegerPart);
    const onScrollDecimal = () => handleScroll(decimalListRef, setDecimalPart);

    const integerEl = integerListRef.current;
    const decimalEl = decimalListRef.current;

    integerEl?.addEventListener("scroll", onScrollInteger);
    if (unit === "inch") {
      decimalEl?.addEventListener("scroll", onScrollDecimal);
    }

    onScrollInteger();
    if (unit === "inch") onScrollDecimal();

    return () => {
      integerEl?.removeEventListener("scroll", onScrollInteger);
      if (unit === "inch") {
        decimalEl?.removeEventListener("scroll", onScrollDecimal);
      }
    };
  }, [show, unit]);

  const scrollToValue = (ref, value) => {
    if (!ref.current) return;
    const container = ref.current;
    const item = container.querySelector(
      `.picker-item:nth-child(${value + 2})`,
    );
    if (item) {
      const containerHeight = container.offsetHeight;
      const itemHeight = item.offsetHeight;
      container.scrollTop =
        item.offsetTop - containerHeight / 2 + itemHeight / 2;
    }
  };

  useEffect(() => {
    if (show) {
      const waist = parseFloat(initialWaist || 0);
      const formattedWaist =
        initialUnit !== unit ? convertWaist(waist, initialUnit, unit) : waist;

      const [intPart, decPart] = formattedWaist.toString().split(".");
      const intVal = parseInt(intPart) || 0;
      const decVal =
        unit === "inch" ? parseInt(decPart?.padEnd(1, "0")) || 0 : 0;

      setIntegerPart(intVal);
      setDecimalPart(decVal);

      setTimeout(() => {
        scrollToValue(integerListRef, intVal);
        if (unit === "inch") scrollToValue(decimalListRef, decVal);
      }, 100);
    }
  }, [show, initialWaist, initialUnit, unit]);

  const handleSave = () => {
    const waist =
      unit === "cm" ? integerPart : parseFloat(`${integerPart}.${decimalPart}`);
    onSave({
      waist_circumference: waist.toFixed(unit === "cm" ? 0 : 1),
      waistUnit: unit,
    });
  };

  const renderScrollPicker = (range, value, setValue, ref) => (
    <div className="picker-wrapper">
      <div className="picker-container" ref={ref}>
        <div className="picker-list">
          <div style={{ height: "40px" }} />
          {range.map((num) => (
            <div
              key={num}
              tabIndex={-1}
              className={`picker-item ${value === num ? "active" : ""}`}
            >
              {num}
            </div>
          ))}
          <div style={{ height: "40px" }} />
        </div>
      </div>
      <div className="picker-border" />
    </div>
  );

  const getIntegerRange = () => {
    return unit === "cm" ? [...Array(201).keys()] : [...Array(101).keys()];
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title className="h5">
          {screenLabels?.edit_waist?.label_value || "Edit Waist"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex justify-content-center mb-3">
          <div
            className="btn-group w-100 border-primary"
            role="group"
            style={{ borderRadius: "10px", overflow: "hidden" }}
          >
            {["cm", "inch"].map((u) => (
              <React.Fragment key={u}>
                <input
                  type="radio"
                  className="btn-check"
                  name="waistUnit"
                  id={u}
                  checked={unit === u}
                  onChange={() => setUnit(u)}
                />
                <label
                  className={`btn py-2 fw-semibold d-flex align-items-center justify-content-center border-0 ${
                    unit === u ? "bg-primary text-white" : "bg-light"
                  }`}
                  htmlFor={u}
                  style={{ flex: 1 }}
                >
                  {authLabel?.[u]?.label_value || u.toUpperCase()}
                </label>
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="d-flex justify-content-center gap-3 align-items-center position-relative">
          {renderScrollPicker(
            getIntegerRange(),
            integerPart,
            setIntegerPart,
            integerListRef,
          )}
          {unit === "inch" && (
            <>
              <div className="fs-3 fw-bold mb-4">.</div>
              {renderScrollPicker(
                [...Array(10).keys()],
                decimalPart,
                setDecimalPart,
                decimalListRef,
              )}
            </>
          )}
          <div className="fs-4 d-flex align-items-center mb-4">
            {authLabel?.[unit]?.label_value}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-danger" onClick={onClose}>
          {screenLabels?.cancel?.label_value || "Cancel"}
        </button>
        <button className="btn btn-primary" onClick={handleSave}>
          {screenLabels?.save?.label_value || "Save"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default WaistModal;
